class VariantPickerDeltas {
  constructor(container) {
    this.container = container;
    this.products = [];
    this.boundOnChange = this.onChange.bind(this);
  }

  connectedCallback() {
    this.render();
    this.container.addEventListener('change', this.boundOnChange);
  }

  disconnectedCallback() {
    this.container.removeEventListener('change', this.boundOnChange);
  }

  onChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (!target.classList.contains('smart_radio')) return;
    if (!target.hasAttribute('data-price-delta-cents')) return;

    if (this.products.length) {
      this.recomputeFromProducts(this.products);
      return;
    }

    this.render();
  }

  getCurrencyFormatter() {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: window?.Shopify?.currency?.active || 'EUR',
    });
  }

  formatMoney(cents) {
    return this.getCurrencyFormatter().format(Number(cents) / 100);
  }

  getSelectedOptions() {
    const selected = {};
    const checkedRadios = this.container.querySelectorAll('.smart_radio:checked');

    for (const radio of checkedRadios) {
      const labelName = radio.dataset.label_name;
      if (!labelName) continue;
      selected[labelName] = radio.value;
    }

    return selected;
  }

  findMatchingProduct(products, targetOptions) {
    const targetKeys = Object.keys(targetOptions);
    if (!targetKeys.length) return null;

    return products.find((entry) => {
      const options = entry?.options;
      if (!options) return false;
      return targetKeys.every((key) => options[key] === targetOptions[key]);
    }) || null;
  }

  getProductPriceCents(entry) {
    if (!entry) return null;
    const amount = entry.price_amount ?? entry.product_price_amount;
    if (amount == null || amount === '') return null;
    const cents = Number(amount);
    return Number.isFinite(cents) ? cents : null;
  }

  recomputeFromProducts(products) {
    if (!Array.isArray(products) || !products.length) return;

    this.products = products;
    const selectedOptions = this.getSelectedOptions();
    const selectedProduct = this.findMatchingProduct(products, selectedOptions);
    const selectedPriceCents = this.getProductPriceCents(selectedProduct);

    const fieldsets = this.container.querySelectorAll('fieldset');

    for (const fieldset of fieldsets) {
      const radios = fieldset.querySelectorAll('.smart_radio[data-price-delta-cents]');
      if (!radios.length) continue;

      for (const radio of radios) {
        const labelName = radio.dataset.label_name;
        if (!labelName) continue;

        const targetOptions = { ...selectedOptions, [labelName]: radio.value };
        const match = this.findMatchingProduct(products, targetOptions);
        const matchPriceCents = this.getProductPriceCents(match);

        if (matchPriceCents != null) {
          radio.dataset.priceDeltaCents = String(matchPriceCents);
        } else if (selectedPriceCents != null) {
          // No sibling for this combo — blank relative delta (avoid false -selected).
          radio.dataset.priceDeltaCents = String(selectedPriceCents);
        }
      }
    }

    this.render();
  }

  render() {
    const fieldsets = this.container.querySelectorAll('fieldset');

    for (const fieldset of fieldsets) {
      const radios = fieldset.querySelectorAll('.smart_radio[data-price-delta-cents]');
      if (!radios.length) continue;

      const checkedRadio = fieldset.querySelector('.smart_radio[data-price-delta-cents]:checked');
      const selectedCents = Number(checkedRadio?.dataset.priceDeltaCents || 0);

      for (const radio of radios) {
        const label = radio.nextElementSibling;
        const priceEl = label?.querySelector('[data-option-price-label]');
        if (!priceEl || !(label instanceof HTMLElement)) continue;

        const optionCents = Number(radio.dataset.priceDeltaCents || 0);
        const diffCents = optionCents - selectedCents;
        const isSelected = radio.checked;
        const isNegative = diffCents < 0;

        label.classList.toggle('is-selected', isSelected);
        priceEl.classList.toggle('variant-picker-deltas__option-price--negative', !isSelected && isNegative);

        if (isSelected || diffCents === 0) {
          priceEl.textContent = '';
          continue;
        }

        const sign = diffCents > 0 ? '+' : '-';
        priceEl.textContent = `${sign}${this.formatMoney(Math.abs(diffCents))}`;
      }
    }
  }
}

const variantPickerDeltaInstances = new WeakMap();

function initVariantPickerDeltas(root = document) {
  const containers = root.querySelectorAll('.variant-picker-deltas[data-show-price-deltas]');

  for (const container of containers) {
    if (!(container instanceof HTMLElement)) continue;

    let instance = variantPickerDeltaInstances.get(container);
    if (!instance) {
      instance = new VariantPickerDeltas(container);
      variantPickerDeltaInstances.set(container, instance);
      instance.connectedCallback();
    } else {
      instance.render();
    }
  }
}

function recomputeVariantPickerDeltas(products, root = document) {
  if (!Array.isArray(products) || !products.length) return;

  initVariantPickerDeltas(root);
  const containers = root.querySelectorAll('.variant-picker-deltas[data-show-price-deltas]');

  for (const container of containers) {
    if (!(container instanceof HTMLElement)) continue;
    const instance = variantPickerDeltaInstances.get(container);
    if (!instance) continue;
    instance.recomputeFromProducts(products);
  }
}

window.initVariantPickerDeltas = initVariantPickerDeltas;
window.recomputeVariantPickerDeltas = recomputeVariantPickerDeltas;

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initVariantPickerDeltas());
} else {
  initVariantPickerDeltas();
}

window.addEventListener('load', () => initVariantPickerDeltas());

document.addEventListener('istorm:variant-picker-deltas-update', () => initVariantPickerDeltas());

document.addEventListener('istorm:variant-picker-deltas-recompute', (event) => {
  const products = event?.detail?.products;
  recomputeVariantPickerDeltas(products);
});
